/**
 * OAuth Error Callback Page
 *
 * Handles failed OAuth authentication attempts
 * Shows user-friendly error message and allows retry
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';

export const OAuthError: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState('Authentication failed');
  const [provider, setProvider] = useState('');

  useEffect(() => {
    const error = searchParams.get('error');
    const providerParam = searchParams.get('provider');
    const errorDetail = searchParams.get('detail');

    setProvider(providerParam || 'OAuth');

    // Map error codes to user-friendly messages
    const errorMessages: Record<string, string> = {
      'access_denied': 'You cancelled the authorization. Please try again if you want to continue.',
      'email_not_found': 'We couldn\'t retrieve your email address. Please ensure your email is public or verified with ' + (providerParam || 'the provider') + '.',
      'invalid_credentials': 'Invalid OAuth credentials. Please try signing in with email/password or contact support.',
      'oauth_failed': 'Authentication failed. Please try again or use email/password login.',
      'server_error': 'A server error occurred during authentication. Please try again in a few moments.',
      'invalid_state': 'Invalid authentication state. Please start the login process again.',
      'token_error': 'Failed to retrieve authentication token. Please try again.',
    };

    // Use custom error detail if provided, otherwise use mapped message
    const displayMessage = errorDetail || errorMessages[error || ''] || 'An unexpected error occurred during authentication. Please try again or use email/password login.';

    setErrorMessage(displayMessage);

    // Log error for debugging
    console.error('OAuth Error:', {
      error,
      provider: providerParam,
      detail: errorDetail,
      fullUrl: window.location.href
    });

  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Failed
            </h2>

            <p className="text-gray-600 mb-6">
              {errorMessage}
            </p>

            {provider && (
              <p className="text-sm text-gray-500 mb-6">
                Provider: <span className="font-medium capitalize">{provider}</span>
              </p>
            )}

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/auth/login')}
                variant="primary"
                className="w-full"
              >
                Try Again
              </Button>

              <Button
                onClick={() => navigate('/auth/register')}
                variant="outline"
                className="w-full"
              >
                Create Account with Email
              </Button>
            </div>

            <p className="text-xs text-gray-500 mt-6">
              If this problem persists, please contact{' '}
              <a href="mailto:support@cleveract.ai" className="text-blue-600 hover:underline">
                support@cleveract.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthError;
